import Layout from "@/components/feature/Layout";
import PageHero from "@/components/base/PageHero";
import SectionHeader from "@/components/base/SectionHeader";
import CTAPanel from "@/components/base/CTAPanel";
import NAICSCodesSection from "@/components/base/NAICSCodesSection";

const CAPABILITY_PDF_URL = "https://storage.helloreaddy.io/project_files/1b8b6e18-bd05-4772-a2fe-587c059d8c1c/946b59fd-8d37-4106-a03e-2537a837a668_Mitaja_Capability_Statement.pdf";

const coreCapabilities = [
  {
    title: "Enterprise Infrastructure & Network Engineering",
    description: "LAN/WAN engineering, server and collaboration support, VMware/vCenter infrastructure, deployment, migration and modernization support.",
  },
  {
    title: "Database & Data Platform Services",
    description: "Oracle and SQL database engineering, administration, schema support, data integrity, reporting and platform operations.",
  },
  {
    title: "Application & Platform Engineering",
    description: "Enterprise software support, WebSphere, application integration, data development and database-backed business systems.",
  },
  {
    title: "Technical Staffing & Delivery Teams",
    description: "Engineers, DBAs, developers, analysts and subject-matter experts aligned to mission-focused delivery teams.",
  },
  {
    title: "Microsoft Power Platform & SharePoint",
    description: "Power Apps, Power Automate, Power BI and SharePoint support, including integration, testing, training and user adoption.",
  },
];

const documentedTechnologies = [
  "LAN/WAN", "VMware/vCenter", "Oracle", "SQL Server", "WebSphere", "SharePoint", "Power Apps", "Power Automate", "Power BI",
];

const selectedExperience = [
  {
    client: "SSA Enterprise IT Support",
    type: "Federal Subcontract",
    description: "Supported SSA environments across multiple subcontract engagements with LAN/WAN engineering, server and collaboration support, virtualization, database administration, enterprise software support and senior technical resources.",
  },
  {
    client: "CDC Business Applications",
    type: "Federal Subcontract",
    description: "Provided Power Platform and SharePoint SME support for a CDC NCCDPHP business-application environment, including Power Apps, Power Automate, Power BI, integration, testing and training.",
  },
  {
    client: "Georgia Local Government",
    type: "Subcontract",
    description: "Provided application integration analyst support for a Georgia local-government client.",
  },
  {
    client: "Commercial Technical Services",
    type: "Subcontract",
    description: "Provided technical resources supporting airline, healthcare and enterprise technology engagements through established delivery partners.",
  },
  {
    client: "India Public-Sector Programs",
    type: "Partner-Supported",
    description: "Provided consulting support to Siyana Info Solutions across transportation, utilities, forestry, higher education and government digital-service initiatives. End-client references remain attributable to prime partners.",
  },
];

export default function CapabilityStatement() {
  return (
    <Layout>
      <PageHero
        title="Capability Statement"
        subtitle="Mitaja Corporation"
        description="Enterprise IT engineering, database & platform services, and delivery teams for complex public-sector and commercial environments."
        size="md"
      />

      <section className="section-padding bg-background-50">
        <div className="container-page">
          <div className="max-w-4xl mx-auto">
            <div className="card-base p-8 md:p-10 mb-14">
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-6">
                <div>
                  <h2 className="text-lg font-bold text-foreground-900 mb-1">
                    Mitaja Corporation Capability Statement
                  </h2>
                  <p className="text-sm text-foreground-600">
                    PDF version available for download or procurement review.
                  </p>
                </div>
                <a
                  href={CAPABILITY_PDF_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary whitespace-nowrap inline-flex items-center"
                  aria-label="Download Mitaja Corporation Capability Statement (PDF)"
                >
                  <i className="ri-download-line mr-2" aria-hidden="true"></i>
                  Download PDF
                  <span className="sr-only"> (PDF)</span>
                </a>
              </div>
              <div className="mt-6 flex items-start gap-3 rounded-md bg-secondary-50 border border-secondary-200 p-4" role="note">
                <i className="ri-file-text-line text-secondary-600 text-lg mt-0.5 shrink-0" aria-hidden="true"></i>
                <p className="text-sm text-secondary-800 leading-relaxed">
                  <span className="font-semibold">Accessible version:</span> The full content below on this page is
                  provided as an accessible HTML alternative to the PDF. The PDF is intended to be read, tagged, and
                  navigable by assistive technology; it remains under review for document accessibility (including
                  document title, language, headings, reading order, and tags) by qualified reviewers.
                </p>
              </div>
            </div>

            <SectionHeader
              title="Company Profile"
              description="Verified contracting credentials and business status."
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-14">
              {[
                { label: "Legal Name", value: "Mitaja Corporation" },
                { label: "Business Status", value: "Small, for-profit corporation" },
                { label: "UEI", value: "C9PPAQCM5DD9" },
                { label: "CAGE Code", value: "7PBY4" },
                { label: "SBA 8(a)", value: "Certified participant" },
                { label: "GSA MAS", value: "47QTCA22D0083" },
                { label: "Previous GSA Schedule 70", value: "47QTCA18D00DQ (expired June 17, 2023)" },
                { label: "Address", value: "8115 Maple Lawn Blvd., Suite 350, Fulton, MD 20759-2683" },
              ].map((field) => (
                <div
                  key={field.label}
                  className="bg-background-100 rounded-lg p-4 border border-background-200/70"
                >
                  <p className="text-xs font-semibold uppercase tracking-wider text-foreground-500 mb-1">
                    {field.label}
                  </p>
                  <p className="text-sm font-medium text-foreground-900">{field.value}</p>
                </div>
              ))}
            </div>

            <div className="mb-14">
              <NAICSCodesSection />
            </div>

            <SectionHeader
              title="State Certifications"
              description="Certified small and diverse business designations for state and local procurement."
            />
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-14">
              {[
                {
                  abbr: "MBE",
                  name: "Minority Business Enterprise",
                  description: "Maryland-certified Minority Business Enterprise (MBE) — Certification No. 17-059 for state and local government contracting.",
                },
                {
                  abbr: "DBE",
                  name: "Disadvantaged Business Enterprise",
                  description: "Maryland-certified Disadvantaged Business Enterprise (DBE) — Certification No. 17-059 supporting DOT-assisted and transportation-related procurements.",
                },
                {
                  abbr: "SBE",
                  name: "Small Business Enterprise",
                  description: "Maryland-certified Small Business Enterprise (SBE) — Certification No. 17-059 for small-business set-aside and preference programs.",
                },
              ].map((cert) => (
                <div key={cert.abbr} className="card-base p-5">
                  <div className="w-12 h-12 rounded-md bg-accent-100 flex items-center justify-center mb-3">
                    <span className="text-sm font-bold text-accent-700">{cert.abbr}</span>
                  </div>
                  <h3 className="text-sm font-bold text-foreground-900 mb-1">{cert.name}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{cert.description}</p>
                </div>
              ))}
            </div>

            <SectionHeader
              title="Core Capabilities"
              description="Enterprise IT engineering and specialist technical services."
            />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-14">
              {coreCapabilities.map((cap) => (
                <div key={cap.title} className="card-base p-5">
                  <h3 className="text-sm font-bold text-foreground-900 mb-1">{cap.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{cap.description}</p>
                </div>
              ))}
            </div>

            <SectionHeader
              title="Differentiators"
              description="What distinguishes Mitaja in competitive procurement environments."
            />
            <div className="card-base p-6 mb-14">
              <ul className="space-y-3 text-sm text-foreground-700 leading-relaxed">
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-accent-600 mt-0.5 shrink-0"></i>
                  Multi-year experience supporting federal enterprise IT environments through higher-tier partners.
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-accent-600 mt-0.5 shrink-0"></i>
                  Depth across infrastructure, databases, platforms and specialist technical labor.
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-accent-600 mt-0.5 shrink-0"></i>
                  Conservative, evidence-controlled approach to past-performance and contract claims.
                </li>
                <li className="flex items-start gap-3">
                  <i className="ri-check-line text-accent-600 mt-0.5 shrink-0"></i>
                  Partner-supported international consulting exposure across public-sector digital programs.
                </li>
              </ul>
            </div>

            <SectionHeader
              title="Selected Experience"
              description="Documented subcontract performance and partner-supported engagements."
            />
            <div className="space-y-4 mb-14">
              {selectedExperience.map((exp) => (
                <div key={exp.client} className="card-base p-5">
                  <div className="flex items-center gap-2 mb-1.5">
                    <span className="text-xs font-bold uppercase tracking-wider text-secondary-700 bg-secondary-100 rounded px-2 py-0.5">
                      {exp.type}
                    </span>
                  </div>
                  <h3 className="text-sm font-bold text-foreground-900 mb-1">{exp.client}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{exp.description}</p>
                </div>
              ))}
            </div>

            <SectionHeader
              title="Documented Technologies"
              description="Delivery model and technology stack."
            />
            <div className="card-base p-6 mb-14">
              <div className="flex flex-wrap gap-2">
                {documentedTechnologies.map((tech) => (
                  <span
                    key={tech}
                    className="inline-flex items-center px-3 py-1.5 rounded-md bg-secondary-100 text-secondary-900 text-sm font-medium"
                  >
                    {tech}
                  </span>
                ))}
              </div>
            </div>

            <CTAPanel
              title="Download Full Capability Statement"
              description="The complete PDF includes company profile, service descriptions, past-performance summaries, and verified contracting credentials."
              ctaText="Download PDF"
              ctaLink={CAPABILITY_PDF_URL}
              ctaExternal
            />
          </div>
        </div>
      </section>
    </Layout>
  );
}